import Button from '@atlaskit/button';
import MoreIcon from '@atlaskit/icon/glyph/more';
import TrayIcon from '@atlaskit/icon/glyph/tray';
import {
  useAnalytics,
  SendDatabaseDeleteEntryEventSources,
  sendDeleteEntryEvent,
} from '@databases/analytics';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
  DropdownMenuProps,
  OnOpenChangeArgs,
} from '@databases/atlaskit/DropdownMenu';
import { useDialog } from '@databases/dialog';
import { openPrintEntryPopup } from '@databases/export';
import { useFlaggingCallback } from '@databases/flags';
import { TrashIcon } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft, saveDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { getDatabaseEntryRequired, getEntryName } from '@databases/utils/database/utils';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { getIdentity } from '@canvas/connection/identity';

import { useScope } from '../Scope';
import SlideTransition from '../SlideTransition';
import { getSelectDatabaseDraft } from '../database-value/utils';

export const ImmediateEntryActionsContainer = styled.div`
  font-weight: normal;

  &,
  > div {
    display: flex;
    align-items: center;
  }

  > * + * {
    margin-left: 5px;
  }
`;

export type ImmediateEntryActionKey = 'print' | 'openDatabase' | 'discard' | 'delete';

const defaultTrigger: DropdownMenuProps['trigger'] = ({ triggerRef, ...props }) => (
  <Button iconBefore={<MoreIcon label="" />} ref={triggerRef} {...props} />
);

export interface ImmediateEntryActionsProps {
  contentId: string;
  entryId: string;
  placement?: DropdownMenuProps['placement'];
  show?: ImmediateEntryActionKey[];
}

/**
 * Buttons to perform various actions on database entries. Actions are applied immediately when clicking them, rather than scheduling them for later.
 */
const ImmediateEntryActions: FunctionComponent<ImmediateEntryActionsProps> = ({
  contentId,
  entryId,
  placement,
  show,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const client = useAnalytics();

  const [showDevMenu, setShowDevMenu] = useState(false);
  const { showDialog } = useDialog();
  const { getData, hidePanel } = usePanel();
  const { entryId: panelEntryId } = getData<'EntryViewPanel'>() || {};

  useEffect(() => {
    const loadDevMenu = async () => {
      const identity = await getIdentity();
      if (identity.email?.endsWith('@atlassian.com')) {
        setShowDevMenu(true);
      }
    };

    loadDevMenu();
  }, []);

  const intl = useIntl();
  const { formatMessage } = intl;

  const scope = useScope();
  const [operations, entryName] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const entry = getDatabaseEntryRequired(databaseDraft, entryId);
    return [
      databaseDraft.operations,
      getEntryName({ database: databaseDraft, entry }, state),
    ] as const;
  });

  const handleDeleteClick = useFlaggingCallback(async () => {
    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['entry-actions.delete-entry-heading-text']),
      body: formatMessage(messages['entry-actions.delete-entry-body-text'], {
        entryName: entryName
          ? entryName
          : formatMessage(messages['unnamed-entities.unnamed-entry']),
      }),
      yesLabel: formatMessage(messages['entry-actions.delete-button-text']),
      noLabel: formatMessage(messages['entry-actions.cancel-button-text']),
      appearance: 'danger',
      onClose: async (result) => {
        if (result) {
          if (panelEntryId === entryId) {
            hidePanel();
          }
          await sendDeleteEntryEvent(
            client,
            SendDatabaseDeleteEntryEventSources.DELETE_ENTRY_CONFIRM_DIALOG,
            store.getState(),
            contentId
          );
          dispatch(
            addChangeToDraft(contentId, scope, {
              type: DatabaseChangeType.DELETE_ENTRY,
              entryId: entryId,
            })
          );
          await dispatch(saveDraft(contentId, scope, intl));
        }
      },
    });
  }, [
    showDialog,
    formatMessage,
    entryName,
    panelEntryId,
    entryId,
    client,
    store,
    contentId,
    dispatch,
    scope,
    intl,
    hidePanel,
  ]);

  const handlePrint = useFlaggingCallback(async () => {
    await openPrintEntryPopup(
      {
        contentId,
        entryId,
        selectDatabase: getSelectDatabaseDraft(scope, true),
      },
      store.getState()
    );
  }, [contentId, entryId, scope, store]);

  const [isOpen, setIsOpen] = useState(false);
  const handleOpenChange = useFlaggingCallback((attrs: OnOpenChangeArgs) => {
    setIsOpen(attrs.isOpen);
  }, []);

  const actions = {
    delete: (!show || show.includes('delete')) && operations.updateEntries,
    print: !show || show.includes('print'),
  };
  const hasAnyActions = Object.values(actions).filter((a) => a).length > 0;

  return (
    <ImmediateEntryActionsContainer className={isOpen ? 'isOpen' : ''}>
      <SlideTransition isVisible={hasAnyActions}>
        <DropdownMenu
          trigger={defaultTrigger}
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          placement={placement}
        >
          <DropdownItemGroup>
            {actions.delete && (
              <DropdownItem
                elemBefore={<TrashIcon label="" />}
                onClick={handleDeleteClick}
              >
                {formatMessage(messages['entry-actions.delete-button-text'])}
              </DropdownItem>
            )}
          </DropdownItemGroup>
          <DropdownItemGroup>
            {actions.print && (
              <DropdownItem elemBefore={<TrayIcon label="" />} onClick={handlePrint}>
                {formatMessage(messages['entry-actions.print-button-text'])}
              </DropdownItem>
            )}
          </DropdownItemGroup>
          {showDevMenu && (
            <DropdownItemGroup>
              <DropdownItem
                onClick={() => {
                  showDialog('DevtoolsDialog');
                }}
              >
                {formatMessage(messages['actions-menu.devtools'])}
              </DropdownItem>
            </DropdownItemGroup>
          )}
        </DropdownMenu>
      </SlideTransition>
    </ImmediateEntryActionsContainer>
  );
};

export default ImmediateEntryActions;
